import { useState } from 'react'
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ExternalLink,
  Flag,
  MessageSquare,
  RotateCcw
} from 'lucide-react'
import { Link, Navigate } from 'react-router-dom'
import { isCurrentUserAdminSelector } from '@axilon-apps/domains-identity/state'
import {
  useAppSelector,
  useListBugReportsQuery,
  useUpdateBugReportMutation,
  type BugReport,
  type ListBugReportsArgs
} from '@axilon-apps/domains-ontology-ui-state'

import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'

const PAGE_SIZE = 25

type FeedbackFilter = 'open' | 'flagged' | 'resolved' | 'all'

function listArgsForFilter(filter: FeedbackFilter, pageToken: string): ListBugReportsArgs {
  const args: ListBugReportsArgs = { limit: PAGE_SIZE }
  if (pageToken) args.pageToken = pageToken
  switch (filter) {
    case 'open':
      args.status = 'open'
      break
    case 'resolved':
      args.status = 'resolved'
      break
    case 'flagged':
      args.flagged = true
      break
    case 'all':
      break
  }
  return args
}

const FILTERS: { id: FeedbackFilter; label: string }[] = [
  { id: 'open', label: 'Open' },
  { id: 'flagged', label: 'Flagged' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'all', label: 'All' }
]

export function FeedbackPage() {
  const isGlobalAdmin = useAppSelector(isCurrentUserAdminSelector)
  const [filter, setFilter] = useState<FeedbackFilter>('open')
  const [pageTokens, setPageTokens] = useState<string[]>([''])
  const [pendingIds, setPendingIds] = useState<Set<string>>(() => new Set())
  const [updateFailed, setUpdateFailed] = useState(false)
  const pageToken = pageTokens[pageTokens.length - 1]
  const { currentData, isLoading, isFetching, error } = useListBugReportsQuery(
    listArgsForFilter(filter, pageToken),
    {
      skip: !isGlobalAdmin
    }
  )
  const [updateBugReport] = useUpdateBugReportMutation()

  if (!isGlobalAdmin) return <Navigate to="/plant" replace />

  const reports = currentData?.bug_reports ?? []
  const isListPending = isLoading || (isFetching && !currentData)
  const nextPageToken = currentData?.next_page_token
  const showPagination = pageTokens.length > 1 || Boolean(nextPageToken)

  const selectFilter = (next: FeedbackFilter) => {
    setFilter(next)
    setPageTokens([''])
  }

  const patchReport = async (
    report: BugReport,
    body: { status?: BugReport['status']; flagged?: boolean }
  ) => {
    setPendingIds((ids) => new Set(ids).add(report.id))
    setUpdateFailed(false)
    try {
      await updateBugReport({ id: report.id, ...body }).unwrap()
    } catch {
      setUpdateFailed(true)
    } finally {
      setPendingIds((ids) => {
        const next = new Set(ids)
        next.delete(report.id)
        return next
      })
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-[68rem] flex-col gap-4">
      <header>
        <h1 className="m-0 text-xl font-semibold tracking-tight text-ink">Feedback</h1>
        <p className="mt-1 mb-0 text-13 text-ink-3">
          Bugs and feature requests submitted in the current organization.
        </p>
      </header>

      <div className="flex flex-wrap gap-2" role="group" aria-label="Feedback filters">
        {FILTERS.map((item) => (
          <Button
            key={item.id}
            type="button"
            variant={filter === item.id ? 'default' : 'outline'}
            size="sm"
            aria-pressed={filter === item.id}
            onClick={() => selectFilter(item.id)}
          >
            {item.label}
          </Button>
        ))}
      </div>

      {isListPending ? <p className="text-sm text-muted-foreground">Loading feedback…</p> : null}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          Feedback could not be loaded.
        </p>
      ) : null}
      {updateFailed ? (
        <p role="alert" className="text-sm text-destructive">
          Feedback could not be updated.
        </p>
      ) : null}
      {!isListPending && !error && currentData !== undefined && reports.length === 0 ? (
        <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          No feedback matches this filter.
        </p>
      ) : null}

      <div className="grid gap-3" aria-busy={isFetching}>
        {reports.map((report) => {
          const resolved = report.status === 'resolved'
          const busy = pendingIds.has(report.id)
          return (
            <article
              key={report.id}
              className={`rounded-lg border border-border bg-card p-4 shadow-sm ${resolved ? 'opacity-80' : ''}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="m-0 text-base font-semibold text-foreground">{report.title}</h2>
                    <Badge variant={resolved ? 'secondary' : 'outline'}>
                      {resolved ? 'Resolved' : 'Open'}
                    </Badge>
                    {report.flagged ? <Badge variant="default">Flagged</Badge> : null}
                  </div>
                  <time className="text-xs text-muted-foreground" dateTime={report.created_at}>
                    {new Date(report.created_at).toLocaleString()}
                  </time>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => void patchReport(report, { flagged: !report.flagged })}
                  >
                    <Flag className="size-3.5" />
                    {report.flagged ? 'Unflag' : 'Flag'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      void patchReport(report, { status: resolved ? 'open' : 'resolved' })
                    }
                  >
                    {resolved ? <RotateCcw className="size-3.5" /> : <Check className="size-3.5" />}
                    {resolved ? 'Reopen' : 'Mark resolved'}
                  </Button>
                  <Button asChild variant="outline" size="sm">
                    <Link to={report.page_path}>
                      <ExternalLink className="size-3.5" />
                      Open page
                    </Link>
                  </Button>
                  {report.conversation_id ? (
                    <Button asChild variant="outline" size="sm">
                      <Link to={`/chat/${encodeURIComponent(report.conversation_id)}`}>
                        <MessageSquare className="size-3.5" />
                        Open conversation
                      </Link>
                    </Button>
                  ) : null}
                </div>
              </div>

              <p className="mt-3 mb-0 whitespace-pre-wrap text-sm text-foreground">
                {report.description}
              </p>

              <dl className="mt-4 grid gap-x-6 gap-y-1 border-t border-border pt-3 text-xs text-muted-foreground sm:grid-cols-2">
                <div className="flex min-w-0 gap-2">
                  <dt className="font-medium text-foreground">Reporter</dt>
                  <dd className="m-0 truncate font-mono">{report.reporter_user_id}</dd>
                </div>
                <div className="flex min-w-0 gap-2">
                  <dt className="font-medium text-foreground">Organization</dt>
                  <dd className="m-0 truncate font-mono">{report.organization_id}</dd>
                </div>
                <div className="flex min-w-0 gap-2 sm:col-span-2">
                  <dt className="font-medium text-foreground">Page</dt>
                  <dd className="m-0 truncate font-mono">{report.page_path}</dd>
                </div>
              </dl>
            </article>
          )
        })}
      </div>

      {showPagination ? (
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={pageTokens.length === 1 || isFetching}
            onClick={() => setPageTokens((tokens) => tokens.slice(0, -1))}
          >
            <ArrowLeft className="size-3.5" />
            Newer
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!nextPageToken || isFetching}
            onClick={() => {
              if (nextPageToken) {
                setPageTokens((tokens) => [...tokens, nextPageToken])
              }
            }}
          >
            Older
            <ArrowRight className="size-3.5" />
          </Button>
        </div>
      ) : null}
    </div>
  )
}
